import { getTokens } from './auth-client';
import { ACCESS_TOKEN_LIFE, domain } from './auth-constants';

// Split out of auth.ts so it can be imported from fetch-utils.ts (for recoverable-auth-error
// handling in BpmLambdaFetch) without creating an import cycle — auth.ts itself imports
// UsersLambdaFetch/awaitJSON from fetch-utils.ts for signInAWS's /checkUserAuthorization call.
// Nothing in this file depends on fetch-utils.

// Shared options for every auth cookie we set. `secure` is conditional on the page actually being
// served over HTTPS: local dev runs on plain http://localhost, and a Secure cookie is silently
// dropped (never set) on a non-HTTPS origin — that would break local login if hardcoded true.
// `sameSite: 'strict'` is defense-in-depth: these cookies are never sent ambiently by the browser
// (fetch-utils reads the value via JS and attaches it as an explicit Authorization header), so
// SameSite doesn't affect the OAuth redirect flow or any request that actually needs this cookie.
export const authCookieOptions = () => ({
  domain,
  expires: new Date(Date.now() + ACCESS_TOKEN_LIFE),
  secure: typeof window !== 'undefined' && window.location.protocol === 'https:',
  sameSite: 'strict' as const
});

// PLATFORM-988: tokens now live only in auth-client's module memory, never in a readable cookie —
// this no longer writes AuthCookies.ACCESS_TOKEN/ACCESS_TOKEN_EXPIRATION/LOGIN_METHOD. The
// signature is unchanged (existing callers — redux/user.ts's fetchMe retry, auth.ts's
// periodicallyRefreshGoogleOrCognito) still just need "make sure the session is fresh" with no
// return value. auth-client's own getTokens()
// already de-duplicates concurrent forceRefresh callers (see its refreshInFlight), so this needs
// no in-flight tracking of its own — it's a thin, signature-preserving adapter.
export const refreshCognitoToken = async (): Promise<void> => {
  await getTokens({ forceRefresh: true });
};

// Captured once at module load: true when the URL carries both `code` and `state` query params.
// This app does not read them (the backend's /auth/callback exchanges the code). The flag only makes
// a load with that URL shape skip its boot check, destination capture and return_to follow, and
// keeps App on the loading view, so such a URL never reaches the login form or a redirect.
export const oauthRedirectInProgress =
  typeof window !== 'undefined' &&
  new URLSearchParams(window.location.search).has('code') &&
  new URLSearchParams(window.location.search).has('state');

// sessionStorage key holding the path the user was trying to reach when login kicked in, so we can
// return them there afterward instead of always dumping them at the app root.
const POST_LOGIN_REDIRECT_KEY = 'post_login_redirect';

const MAX_RETURN_TO_LENGTH = 1024;

// Same-origin path rules shared with the backend's `return_to` check: starts with '/', no '//', no
// backslash (browsers read `/\evil.com` as `//evil.com`), printable ASCII only, bounded length.
export const isSafeReturnTo = (candidate: string): boolean =>
  candidate.length <= MAX_RETURN_TO_LENGTH &&
  /^\/(?!\/)[\x21-\x7e]*$/.test(candidate) &&
  !candidate.includes('\\');

const stripHash = (path: string): string => {
  const i = path.indexOf('#');
  return i === -1 ? path : path.slice(0, i);
};

const isRootPath = (pathname: string): boolean => pathname === '/' || pathname === '';

// A safe `return_to` query param (hash dropped), or undefined.
const readReturnToParam = (): string | undefined => {
  const raw = new URLSearchParams(window.location.search).get('return_to');
  if (raw === null) return undefined;
  const candidate = stripHash(raw);
  return isSafeReturnTo(candidate) ? candidate : undefined;
};

const readStash = (): string | undefined => {
  try {
    const stashed = sessionStorage.getItem(POST_LOGIN_REDIRECT_KEY);
    if (!stashed) return undefined;
    const candidate = stripHash(stashed);
    return isSafeReturnTo(candidate) ? candidate : undefined;
  } catch {
    return undefined;
  }
};

// Records the post-login destination. Call once at startup, before any auth redirect. Skips a load whose
// URL carries `code` and `state` params, so it can't overwrite a destination captured earlier. On a root load it stashes a
// safe `return_to` param (a deep link handed over by another app) and otherwise clears the stash, so
// a stale deep link can't linger and hijack a later login.
export const captureIntendedDestination = () => {
  if (typeof window === 'undefined') return;
  if (oauthRedirectInProgress) return;
  const { pathname, search, hash } = window.location;
  try {
    if (isRootPath(pathname)) {
      const returnTo = readReturnToParam();
      if (returnTo && returnTo !== '/') {
        sessionStorage.setItem(POST_LOGIN_REDIRECT_KEY, returnTo);
      } else {
        sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY);
      }
    } else {
      sessionStorage.setItem(POST_LOGIN_REDIRECT_KEY, `${pathname}${search}${hash}`);
    }
  } catch {
    // sessionStorage unavailable (e.g. private mode): fall back to a plain root redirect.
  }
};

/** The path to return to after sign-in: on '/', a safe `return_to` query param; otherwise the
 * current path+search if it's a safe same-origin path other than '/'; else the stashed path; else
 * '/'. Safe means: starts with '/', no '//', no backslash, printable ASCII, at most 1024 chars.
 * Never includes a #hash. */
export const resolvePostLoginPath = (): string => {
  if (typeof window === 'undefined') return '/';
  const { pathname, search } = window.location;
  if (isRootPath(pathname)) return readReturnToParam() ?? readStash() ?? '/';
  const current = `${pathname}${search}`;
  return isSafeReturnTo(current) ? current : (readStash() ?? '/');
};

export const loginRedirect = () => {
  // Navigate to a clean URL rather than reloading: a reload would keep any ?code=&state= OAuth
  // callback params and replay an already-consumed code exchange.
  const target = `${window.location.origin}${resolvePostLoginPath()}`;
  try {
    sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY);
  } catch {
    // sessionStorage unavailable: nothing to clear.
  }
  window.location.replace(target);
};

/** On an already-signed-in boot at '/', navigates (replace) to a safe stashed `return_to` and
 * returns true; otherwise false. */
export const followReturnToAfterBoot = (): boolean => {
  if (typeof window === 'undefined' || oauthRedirectInProgress) return false;
  if (!isRootPath(window.location.pathname)) return false;
  const stashed = readStash();
  if (!stashed || stashed === '/') return false;
  try {
    sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY);
  } catch {
    // sessionStorage unavailable: the navigation below still works.
  }
  window.location.replace(`${window.location.origin}${stashed}`);
  return true;
};
